import React, { useState } from 'react'
import { Loader2 } from 'lucide-react'
import broadcastAdminService, { AdjustmentReason } from '../../services/broadcastAdminService'
import { REASONS } from './broadcastConstants'

type Notify = (title: string, message: string) => void

interface Base {
  customerId: string
  saving: boolean
  setSaving: (v: boolean) => void
  onDone: () => Promise<void> | void
  onSuccess: Notify
  onError: Notify
}

const input =
  'w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors'
const label = 'block text-xs font-medium text-gray-700 mb-1'

/**
 * A period is a CONTRACT TERM, not a calendar month. Defaulting to the season
 * rather than the month is the point: hours that span the whole contract remove
 * the rollover question entirely, instead of expiring at a month boundary a
 * club never agreed to.
 */
export const AllowanceForm: React.FC<Base> = ({ customerId, saving, setSaving, onDone, onSuccess, onError }) => {
  const today = new Date().toISOString().slice(0, 10)
  const [periodStart, setPeriodStart] = useState(today)
  // deliberately blank: a period is a CONTRACT TERM, and guessing an end date
  // is how a season silently becomes a calendar year nobody agreed to
  const [periodEnd, setPeriodEnd] = useState('')
  const [periodLabel, setPeriodLabel] = useState('')
  const [credits, setCredits] = useState('25')
  const [notes, setNotes] = useState('')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!periodEnd) {
      onError('Period end required', 'Set the end of the contract term')
      return
    }
    if (new Date(periodEnd) <= new Date(periodStart)) {
      onError('Invalid period', 'Period end must be after period start')
      return
    }
    setSaving(true)
    try {
      await broadcastAdminService.createAllowance(customerId, {
        periodStart, periodEnd,
        periodLabel: periodLabel || null,
        creditsPurchased: parseInt(credits, 10) || 0,
        notes: notes || null
      })
      onSuccess('Period created', `${credits} credits for ${periodLabel || 'the period'}`)
      await onDone()
    } catch (err) {
      // the API refuses overlapping periods: enforcement reads "the period
      // containing today", so overlap would make that ambiguous
      onError('Could not create period', err instanceof Error ? err.message : 'Please try again')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={submit} className="bg-blue-50/40 border border-blue-200 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className={label}>Period start <span className="text-red-500">*</span></label>
          <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className={input} required />
        </div>
        <div>
          <label className={label}>Period end <span className="text-red-500">*</span></label>
          <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className={input} required />
        </div>
        <div>
          <label className={label}>Label</label>
          <input value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} placeholder="e.g. 2026 season" className={input} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label}>Credits purchased <span className="text-red-500">*</span></label>
          <input type="number" min={0} value={credits} onChange={(e) => setCredits(e.target.value)} className={input} required />
          <p className="text-xs text-gray-500 mt-1">1 credit = one broadcast up to 4 hours.</p>
        </div>
        <div>
          <label className={label}>Notes</label>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="PO number, contract ref" className={input} />
        </div>
      </div>
      <p className="text-xs text-gray-600">
        Set the period to the <strong>contract term</strong> — a season, not a calendar month.
        Credits then span the whole term and nothing expires at a boundary the customer never agreed to.
      </p>
      <div className="flex gap-2">
        <button type="submit" disabled={saving}
          className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 transition-all">
          {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Saving…</> : 'Create period'}
        </button>
      </div>
    </form>
  )
}

/**
 * Grants are a SEPARATE record from what was sold. Bumping credits_purchased
 * from 25 to 26 would be quicker and would quietly destroy the record of the
 * sale — so a grant always carries a reason and a note.
 */
export const AdjustmentForm: React.FC<Base> = ({ customerId, saving, setSaving, onDone, onSuccess, onError }) => {
  const [credits, setCredits] = useState('1')
  const [reasonCode, setReasonCode] = useState<AdjustmentReason>('degraded_content')
  const [notes, setNotes] = useState('')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const n = parseFloat(credits)
    if (!n) { onError('Invalid adjustment', 'Credits must be non-zero'); return }
    if (notes.trim().length < 3) { onError('Reason required', 'Every adjustment needs a note explaining it'); return }
    setSaving(true)
    try {
      await broadcastAdminService.createAdjustment(customerId, { credits: n, reasonCode, notes: notes.trim() })
      onSuccess('Credits adjusted', `${n > 0 ? '+' : ''}${n} credit${Math.abs(n) === 1 ? '' : 's'}`)
      await onDone()
    } catch (err) {
      onError('Could not adjust credits', err instanceof Error ? err.message : 'Please try again')
    } finally {
      setSaving(false)
    }
  }

  const hint = REASONS.find((r) => r.value === reasonCode)?.hint

  return (
    <form onSubmit={submit} className="bg-emerald-50/40 border border-emerald-200 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className={label}>Credits <span className="text-red-500">*</span></label>
          <input type="number" step={0.5} value={credits} onChange={(e) => setCredits(e.target.value)} className={input} required />
          <p className="text-xs text-gray-500 mt-1">Negative corrects an over-grant.</p>
        </div>
        <div className="col-span-2">
          <label className={label}>Reason <span className="text-red-500">*</span></label>
          <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value as AdjustmentReason)} className={input}>
            {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
        </div>
      </div>
      <div>
        <label className={label}>Note <span className="text-red-500">*</span></label>
        <input value={notes} onChange={(e) => setNotes(e.target.value)}
               placeholder="e.g. 60% of the broadcast was rain delay" className={input} required minLength={3} />
        <p className="text-xs text-gray-500 mt-1">
          Required. This is the row that explains why revenue and usage disagree, so it has to say why.
        </p>
      </div>
      <button type="submit" disabled={saving}
        className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white text-xs font-medium rounded-lg hover:from-emerald-700 hover:to-emerald-800 disabled:opacity-50 transition-all">
        {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Saving…</> : 'Grant credits'}
      </button>
    </form>
  )
}
